/**
 * @fileoverview Single source of truth for whether a violation occurrence is
 *   visible in generated report output.
 * @module document-render/occurrenceVisibility
 *
 * Responsibilities: - Decide whether one violation occurrence should appear in
 *                     the rendered report (PDF + web preview), independent of
 *                     whether it is "resolved".
 * Dependencies:     - none (pure).
 * Architecture:     - Framework-agnostic pure predicate. Imported by the worker
 *                     report read model (dataGatherer / documentDataTransformer)
 *                     and the web preview adapter (core-domain
 *                     documentFieldBindings) so the two report paths cannot
 *                     drift on which occurrences are shown.
 * Notes:            - "Report-visible" is a DIFFERENT concept from "unresolved"
 *                     (violationResolution.isOccurrenceUnresolved). A `repaired`
 *                     or `exception` occurrence is resolved yet still legitimately
 *                     appears in the report (shown as repaired/waived). Only an
 *                     occurrence the inspector RETRACTED (`correctionStatus ===
 *                     'retracted'`, the soft-remove tombstone written by
 *                     core-domain occurrenceMutations) or a soft-deleted
 *                     occurrence (`_deleted === true`) is hidden. This is why the
 *                     rule lives here and not in violationResolution.
 */

/**
 * @contract ReportVisibleOccurrence
 * Purpose: Minimal structural shape needed to decide report visibility. Kept
 *          deliberately narrow so any occurrence-like object — the worker's
 *          CanonicalOccurrence or a loose Dexie/preview occurrence — can be
 *          classified without a cast at the call site.
 * Fields:  correctionStatus? — the correction tombstone stamp; `_deleted? —
 *          soft-delete tombstone flag.
 * Rules:   `_deleted === true` OR `correctionStatus === 'retracted'` hides the
 *          occurrence; every other value keeps it visible.
 * Sync:    N/A (pure value classification at render time).
 */
export interface ReportVisibleOccurrence {
  correctionStatus?: string;
  _deleted?: boolean;
}

/** correctionStatus values that remove an occurrence from report output. */
const REPORT_HIDDEN_CORRECTION_STATUSES: ReadonlySet<string> = new Set(['retracted']);

/**
 * @process Decide whether a single occurrence should appear in report output.
 *
 * Steps (first match wins):
 * 1. `_deleted === true` ⇒ hidden (soft-delete tombstone).
 * 2. `correctionStatus === 'retracted'` ⇒ hidden (inspector removed it; the
 *    soft-remove tombstone left by occurrenceMutations must be honored by the
 *    report read model — this is the DFE-007 fix).
 * 3. otherwise ⇒ visible (incl. open / pending / repaired / exception).
 *
 * Inputs:  an occurrence-like object (see {@link ReportVisibleOccurrence}).
 * Outputs: `true` when the occurrence should render, else `false`.
 * Failure: none — pure total function; missing fields ⇒ visible.
 * Idempotency: REQUIRED — pure; same input always yields the same result.
 */
export function isOccurrenceReportVisible(
  occurrence: ReportVisibleOccurrence,
): boolean {
  if (occurrence._deleted === true) return false;
  if (
    occurrence.correctionStatus !== undefined &&
    REPORT_HIDDEN_CORRECTION_STATUSES.has(occurrence.correctionStatus)
  ) {
    return false;
  }
  return true;
}
